import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, type UseFormSetError } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { FormSection } from "~/common/components/FormSection";
import { SimpleAutocomplete } from "~/common/components/Select/SimpleAutocompleteSelect";
import type { DropdownEntry } from "~/common/types";
import { PHONE_REGEXP } from "~/common/utils/validations";
import { zRequiredEmail, zRequiredString } from "~/common/utils/schemaUtils";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "~/common/mui";
import { Controller } from "react-hook-form";

const requestAQuoteSchema = z.object({
  company: z.string().optional(),
  firstName: zRequiredString("common.form.validation.required"),
  lastName: zRequiredString("common.form.validation.required"),
  email: zRequiredEmail("common.form.validation.required", "error.invalidEmail"),
  phone: z.string().optional(),
  address: z.object({
    line1: zRequiredString("common.form.validation.required"),
    line2: z.string().optional(),
    postalCode: zRequiredString("common.form.validation.required"),
    city: zRequiredString("common.form.validation.required"),
    country: zRequiredString("common.form.validation.required"),
  }),
  message: zRequiredString("common.form.validation.required"),
});

export type RequestAQuoteFormData = z.infer<typeof requestAQuoteSchema>;

export interface RequestAQuoteDialogProps {
  open: boolean;
  onClose: () => void;
  countryOptions: readonly DropdownEntry<string>[];
  onSubmit: (
    data: RequestAQuoteFormData,
    helpers: {
      setError: UseFormSetError<RequestAQuoteFormData>;
      onClose: () => void;
    },
  ) => Promise<void>;
}

const defaultValues: RequestAQuoteFormData = {
  company: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  address: {
    line1: "",
    line2: "",
    postalCode: "",
    city: "",
    country: "",
  },
  message: "",
};

export const RequestAQuoteDialog = ({ open, onClose, countryOptions, onSubmit }: RequestAQuoteDialogProps) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    setError,
    formState: { isSubmitting, errors },
    reset,
  } = useForm<RequestAQuoteFormData>({
    defaultValues,
    resolver: zodResolver(requestAQuoteSchema),
  });

  const internalSubmit = async (data: RequestAQuoteFormData) => {
    await onSubmit(data, {
      setError,
      onClose: () => {
        reset();
        onClose();
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="desktop-s"
      fullWidth
      slotProps={{
        paper: {
          component: "form",
          onSubmit: handleSubmit(internalSubmit),
          // @ts-expect-error noValidate is a valid form attribute on the paper slot
          noValidate: true,
        },
      }}
    >
      <DialogHeader onClose={isSubmitting ? undefined : onClose}>
        <DialogTitle sx={{ padding: 0 }}>{t("feature.configurator.requestQuote.title")}</DialogTitle>
      </DialogHeader>
      <DialogContent>
        <Stack gap={3}>
          <FormSection title={t("feature.configurator.requestQuote.section.personal")}>
            <Controller
              name="company"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label={t("feature.configurator.requestQuote.field.company")}
                  fullWidth
                  error={!!error}
                  helperText={error?.message ? t(error.message) : ""}
                />
              )}
            />
            <Stack direction={{ phone: "column", "desktop-s": "row" }} gap={2}>
              {(["firstName", "lastName"] as const).map((name) => (
                <Controller
                  key={name}
                  name={name}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label={t(`feature.configurator.requestQuote.field.${name}`)}
                      required
                      fullWidth
                      error={!!error}
                      helperText={error?.message ? t(error.message) : ""}
                    />
                  )}
                />
              ))}
            </Stack>
            <Stack direction={{ phone: "column", "desktop-s": "row" }} gap={2}>
              <Controller
                name="email"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label={t("feature.configurator.requestQuote.field.email")}
                    required
                    fullWidth
                    error={!!error}
                    helperText={error?.message ? t(error.message) : ""}
                  />
                )}
              />
              <Controller
                name="phone"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label={t("feature.configurator.requestQuote.field.phone")}
                    fullWidth
                    error={!!error}
                    helperText={error?.message ? t(error.message) : ""}
                    inputProps={{
                      onBeforeInput: (event) => {
                        const inputEvent = event as unknown as InputEvent;
                        const value = (event.target as HTMLInputElement).value + inputEvent.data;
                        if (!PHONE_REGEXP.test(value.trim())) {
                          event.preventDefault();
                          event.stopPropagation();
                        }
                      },
                    }}
                    autoComplete="tel"
                  />
                )}
              />
            </Stack>
          </FormSection>
          <FormSection title={t("feature.configurator.requestQuote.section.address")}>
            {(["address.line1", "address.line2"] as const).map((name) => (
              <Controller
                key={name}
                name={name}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label={t(
                      name === "address.line1"
                        ? "feature.configurator.requestQuote.field.address"
                        : "feature.configurator.requestQuote.field.addressAdditional",
                    )}
                    required={name === "address.line1"}
                    fullWidth
                    error={!!error}
                    helperText={error?.message ? t(error.message) : ""}
                  />
                )}
              />
            ))}
            <Stack direction={{ phone: "column", "desktop-s": "row" }} gap={2}>
              {(["address.postalCode", "address.city"] as const).map((name) => (
                <Controller
                  key={name}
                  name={name}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label={t(
                        name === "address.postalCode"
                          ? "feature.configurator.requestQuote.field.zipCode"
                          : "feature.configurator.requestQuote.field.city",
                      )}
                      required
                      fullWidth
                      error={!!error}
                      helperText={error?.message ? t(error.message) : ""}
                    />
                  )}
                />
              ))}
            </Stack>
            <Controller
              name="address.country"
              control={control}
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                <SimpleAutocomplete
                  label={`${t("feature.configurator.requestQuote.field.country")} *`}
                  values={[...countryOptions]}
                  fullWidth
                  error={!!error}
                  helperText={error?.message ? t(error.message) : ""}
                  onBlur={onBlur}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </FormSection>
          <Controller
            name="message"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t("feature.configurator.requestQuote.field.message")}
                required
                fullWidth
                multiline
                minRows={2}
                error={!!error}
                helperText={error?.message ? t(error.message) : ""}
              />
            )}
          />
          {errors.root && <Alert severity="error">{errors.root.message}</Alert>}
          <Typography color="text.secondary">{t("configuration.request.paragrapgh.text")}</Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ mt: 3, px: 3, pb: 3 }}>
        <Button variant="text" onClick={onClose} disabled={isSubmitting}>
          {t("feature.configurator.requestQuote.btn.cancel")}
        </Button>
        <Button type="submit" loading={isSubmitting}>
          {t("feature.configurator.requestQuote.btn.send")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
